import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import DetailedActivityStream, { IDetailedActivityEvent } from "../DetailedActivityStream/DetailedActivityStream";
import { ResourceLocation } from "../../Constants/resourceLocations";
import { CommentService } from "../../Services/CommentService";
import { resolveApiErrorMessage } from "../../Util/apiErrorUtil";
import type { FieldValidationRules } from "../../Util/fieldValidation";
import { mapCommentToActivityEvent } from "./commentActivityUtil";
import "./DashlyCommentSection.scss";

export interface DashlyCommentSectionProps {
    location: ResourceLocation;
    locationIdentifier: string;
    reloadToken?: number | string;
    canEdit?: boolean;
    /** May remove anybody's comment, not just their own. Defaults to `canEdit`, the rule of the modules
     *  where whoever may edit the item also moderates it; pass it where commenting and moderating differ. */
    canModerate?: boolean;
    currentUserUuid?: string;
    title?: string;
    emptyMessage?: string;
    commentPlaceholder?: string;
    /** Overrides default comment body limits (e.g. activity-stream-backed project conversation). */
    commentValidation?: FieldValidationRules;
    maxHeight?: string;
    showHeader?: boolean;
    collapsibleComposer?: boolean;
    addCommentButtonLabel?: string;
    className?: string;
    onCommentsChanged?: () => void;
    onCommentsLoaded?: (count: number) => void;
}

const DashlyCommentSection: React.FC<DashlyCommentSectionProps> = ({
    location,
    locationIdentifier,
    reloadToken,
    canEdit = true,
    canModerate = canEdit,
    currentUserUuid,
    title,
    emptyMessage,
    commentPlaceholder,
    commentValidation,
    maxHeight = "260px",
    showHeader = false,
    collapsibleComposer = false,
    addCommentButtonLabel,
    className,
    onCommentsChanged,
    onCommentsLoaded,
}) => {
    const { t } = useTranslation();
    const commentService = useMemo(() => new CommentService(), []);
    const [events, setEvents] = useState<IDetailedActivityEvent[]>([]);
    const [loading, setLoading] = useState(false);

    const loadComments = useCallback(async () => {
        if (!locationIdentifier) {
            setEvents([]);
            onCommentsLoaded?.(0);
            return;
        }
        setLoading(true);
        try {
            const response = await commentService.fetchByLocation(location, locationIdentifier);
            const comments = Array.isArray(response.data.data) ? response.data.data : [];
            const mapped = comments.map(mapCommentToActivityEvent);
            setEvents(mapped);
            onCommentsLoaded?.(mapped.length);
        } catch {
            setEvents([]);
            onCommentsLoaded?.(0);
        } finally {
            setLoading(false);
        }
    }, [commentService, location, locationIdentifier, onCommentsLoaded]);

    useEffect(() => {
        loadComments();
    }, [loadComments, reloadToken]);

    const handleAddComment = async (commentText: string) => {
        if (!canEdit || !commentText.trim()) {
            return;
        }
        try {
            await commentService.addComment(location, locationIdentifier, commentText.trim());
            await loadComments();
            onCommentsChanged?.();
            toast.success(t("common.saved"), { autoClose: 1500 });
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "common.error", t));
        }
    };

    const handleDeleteComment = async (event: IDetailedActivityEvent) => {
        if (!event.uuid) {
            return;
        }
        try {
            await commentService.deleteComment(event.uuid);
            await loadComments();
            onCommentsChanged?.();
            toast.success(t("common.deleted"));
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "workflow.comment.delete.error", t));
        }
    };

    return (
        <div className={className ? `dashly-comment-section ${className}` : "dashly-comment-section"}>
            <DetailedActivityStream
                title={title || t("processflow.detail.comments")}
                events={events}
                loading={loading}
                emptyMessage={emptyMessage || t("processflow.detail.comments.empty")}
                commentPlaceholder={commentPlaceholder || t("workflow.comment.placeholder", { defaultValue: "Voeg een opmerking toe..." })}
                commentValidation={commentValidation}
                maxHeight={maxHeight}
                showHeader={showHeader}
                collapsibleComposer={collapsibleComposer}
                addCommentButtonLabel={addCommentButtonLabel}
                onAddComment={canEdit ? handleAddComment : undefined}
                onDeleteComment={canEdit || canModerate ? handleDeleteComment : undefined}
                canEditComments={canModerate}
                currentUserUuid={currentUserUuid}
            />
        </div>
    );
};

export default DashlyCommentSection;
